import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { Guid, RegisterEntry } from "@/api/types";

export type RegisterStatus = "loading" | "ready" | "error";

export interface UseRegisterResult {
  /** Empty on first load and on failure — same "we don't know yet" vs. "genuinely
   *  nothing" split `useBooksActivity` already draws. */
  entries: RegisterEntry[];
  status: RegisterStatus;
  error: string | null;
  /** True when another page follows the last one loaded. */
  hasMore: boolean;
  /** Fetches the next page (by the opaque cursor `getRegister` returned) and appends it. */
  loadMore: () => Promise<void>;
  /**
   * Re-fetches the FIRST page in place. Called right after `postRegisterEntry` resolves —
   * the write response is just `{ id }` (`RegisterEntryCreatedResponse`), so the new row's
   * server-computed `type`/`runningBalance`/`red` come from re-reading the register, never
   * from a client guess at any of the three (BooksRegisterRepository's own remarks on why
   * that classification is read-side). The demo tenant's own instrument accounts stay well
   * under one page (`BooksRegisterRepository.DefaultPageSize`), so a first-page reload
   * always includes a row just posted with `occurredOn = today`.
   */
  refresh: () => Promise<void>;
}

/**
 * `GET /api/demo/books/register` for one (asset) account — ef-042-1's read, paged with a
 * keyset cursor (`BooksRegisterRepository`'s own remarks on why offset pagination cannot
 * keep a running balance stable). Re-fetches from the first page whenever `accountId`
 * changes.
 *
 * `order: "desc"` on EVERY fetch (ef-048-6) — newest first, so a freshly posted row is on
 * page one regardless of how many same-day rows already exist, rather than a farmer
 * needing to page through the account's whole history to find what they just entered.
 *
 * Owner-only server-side; this hook does not check that and must not — same discipline
 * `useBooksActivity`/`useLabourReport` already follow (a client-side role check is a
 * rendering decision, not the control).
 *
 * `from`/`to` (ef-054-0c) are optional ISO dates forwarded on every `getRegister` call
 * this hook makes — first page AND `loadMore` — same params `client.ts`'s `getRegister`
 * already accepts (no server-side gap). A change to either re-fetches from the first
 * page, same as an `accountId` change.
 */
export function useRegister(accountId: Guid | null, from?: string, to?: string): UseRegisterResult {
  const [entries, setEntries] = useState<RegisterEntry[]>([]);
  const [status, setStatus] = useState<RegisterStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchFirstPage = useCallback(
    async (id: Guid, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      try {
        const page = await demoApi.getRegister({ accountId: id, from, to, order: "desc" }, signal);
        if (signal?.aborted) return;
        setEntries(page.entries);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        // The previous account's rows are cleared, not kept — a stale register for a
        // different account is the one outcome that reads as an answer to this one.
        setEntries([]);
        setCursor(null);
        setHasMore(false);
        setStatus("error");
        setError(problemMessage(err, "Could not load the register."));
      }
    },
    [from, to],
  );

  useEffect(() => {
    if (!accountId) {
      setEntries([]);
      setCursor(null);
      setHasMore(false);
      setStatus("ready");
      setError(null);
      return;
    }
    const controller = new AbortController();
    void fetchFirstPage(accountId, controller.signal);
    return () => controller.abort();
  }, [accountId, fetchFirstPage]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!accountId || !cursor) return;
    try {
      const page = await demoApi.getRegister({ accountId, cursor, from, to, order: "desc" });
      setEntries((prev) => [...prev, ...page.entries]);
      setCursor(page.nextCursor);
      setHasMore(page.nextCursor !== null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(problemMessage(err, "Could not load more of the register."));
    }
  }, [accountId, cursor, from, to]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!accountId) return;
    await fetchFirstPage(accountId);
  }, [accountId, fetchFirstPage]);

  return { entries, status, error, hasMore, loadMore, refresh };
}
